import type { ServiceProvider } from "@expert/service-providers";
import type { KyInstance } from "ky";

import { getFeature } from "@asurion-core/expert-workspace-feature-flagging";
import { getLogger } from "@expert/logging";
import { useQuery } from "@tanstack/react-query";

import type { AppConfig } from "../types";

const logger = getLogger({ module: "useAppConfigAndServiceProviderOverride" });
/** Can ONLY be used under the <AuthProvider> scope
 * Retrieves the app configuration and service provider override
 */
export const useAppConfigAndServiceProviderOverride = (
    api: KyInstance,
    employeeId: string | undefined,
    channel?: string,
) => {
    const { data, isLoading, error, status } = useQuery({
        queryKey: [`app-config`],
        queryFn: async () => {
            // The appConfig fetch and the ServiceProviderOverride lookup are independent, so they
            // run in parallel. If partner-based flag rules are ever added back, the flag context
            // would need the expert's partner (derived from appConfig.allowedPartners), which would
            // force the lookup to await appConfig first and give up this parallelism.
            const [appConfig, serviceProviderOverride] = await Promise.all([
                api.get("api/app-config").json<AppConfig>(),
                getFeature<ServiceProvider>("ServiceProviderOverride", { employeeId, channel }),
            ]);
            logger.info({ channel, serviceProviderOverride }, "ServiceProviderOverride resolved");
            const result: [AppConfig, ServiceProvider | undefined] = [appConfig, serviceProviderOverride];
            return result;
        },
        retry: 0,
        refetchOnWindowFocus: false,
        enabled: !!employeeId,
    });
    if (data) {
        logger.debug("AppConfig received");
    }
    if (error) {
        logger.error({ error }, "Failed to fetch App Configuration");
    }
    return { data, isLoading, error, status };
};
